import Spinner from '@atlaskit/spinner';
import SectionMessage from '@atlaskit/section-message';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import config from 'appconfig';
import t from 'modules/i18n/intl';
import { State } from 'model/State';
import { extractTLDFrom } from 'modules/siteconfigurator/utils';
import { fetchGenericProperty } from 'modules/mac/duck/actions/genericProperty';
import { fetchAccountHistory } from 'modules/mac/duck/actions/accountId';
import {
  getCurrentMACContactEmail,
  getCurrentMACContactOrganizationDetails,
  selectDetails,
  selectDetailsProgress,
  selectGenericProperty,
} from 'modules/mac/duck/selectors';
import { messages } from 'modules/mac/messages';
import { RESTRICTED_EMAIL_DOMAINS } from 'modules/utils';
import DetailsRowContent from './DetailsRowContent';
import { StyledSectionMessageWrapper, StyledTableData } from './StyledComponents';
import { SpinnerWrapper } from '../styledComponents';
import MigratedSubscriptionBanner from './DetailsRowContentElements/MigratedSubscriptionBanner';

export enum ContactTypes {
  TECHNICAL = 'TECHNICAL',
  BILLING = 'BILLING',
}

export type MappedContact = {
  id: string;
  contactId: string;
  name: string;
  email: string;
  main: boolean;
  canBePromoted: boolean;
  inactiveMakePrimary: boolean;
  noMakePrimary: boolean;
  canBeDeleted: boolean;
  smartDomain: string;
};

export const getContacts = (
  accountDetails,
  contactType,
  mainContact,
  userEmail: string,
): MappedContact[] => {
  const contacts = [];

  if (!accountDetails || !accountDetails.contactRelationships) return contacts;
  const relationShips = accountDetails.contactRelationships;

  const currentUserInRelations = relationShips.find(
    (r) => r.contact.contactDetails.email === userEmail,
  );

  return relationShips
    .filter((contact) => contact.relationshipType === contactType)
    .toSorted((a, b) =>
      a.contact.contactDetails.smartDomain.localeCompare(b.contact.contactDetails.smartDomain),
    )
    .map(({ contact, main, id }) => {
      const mainContactDetails = mainContact?.contactDetails;
      const mainContactEmail = mainContactDetails?.email;

      const { contactDetails } = contact;
      const currentContactIsLoggedInUser = contactDetails.email === userEmail;
      const isMainTechnicalContact = userEmail === mainContactEmail;
      const mainSmartDomain = contactDetails.smartDomain === mainContactDetails?.smartDomain;

      const canBePromoted =
        currentContactIsLoggedInUser ||
        (isMainTechnicalContact && !currentContactIsLoggedInUser && mainSmartDomain);

      const inactiveMakePrimary = !currentContactIsLoggedInUser && !mainSmartDomain;

      const noMakePrimary =
        (contactDetails.email !== mainContactEmail && !currentUserInRelations) ||
        (contactDetails.email === mainContactEmail && !!currentUserInRelations) ||
        RESTRICTED_EMAIL_DOMAINS.includes(extractTLDFrom(contactDetails.email));

      const canBeDeleted = !!currentUserInRelations;

      return {
        id,
        contactId: contactDetails.id,
        name: contactDetails.displayName,
        email: contactDetails.email,
        canBePromoted,
        inactiveMakePrimary,
        noMakePrimary,
        main,
        canBeDeleted,
        smartDomain: contactDetails.smartDomain,
      };
    });
};

const getMainContact = (contacts, contactType) => {
  if (!contacts) return null;

  return contacts.find((cr) => cr.main && cr.relationshipType === contactType)?.contact;
};

const getPpcLink = (chunks, transactionAccountId, externalUuid) => (
  <a
    href={`${config.ppcUrl}/account/${transactionAccountId}/cloud-sites/${externalUuid}`}
    target="_blank"
    rel="noreferrer"
  >
    {chunks}
  </a>
);

const Loading = () => (
  <SpinnerWrapper>
    <Spinner size="medium" testId="mac-license-details-spinner" />
  </SpinnerWrapper>
);

const DetailsRowContentWithLoader = ({
  accountDetails,
  technicalContacts,
  billingContacts,
  isAccount,
  isOpen,
  openModal,
  closeModal,
  isArchived,
}) =>
  accountDetails ? (
    <DetailsRowContent
      accountDetails={accountDetails}
      technicalContacts={technicalContacts}
      billingContacts={billingContacts}
      isAccount={isAccount}
      isOpen={isOpen}
      openModal={openModal}
      closeModal={closeModal}
      isArchived={isArchived}
    />
  ) : null;

const LicensesExpandedRow = ({ sen, isAccount, isPartner, isArchived }) => {
  let genericPropertyFullKey = '';

  const dispatch = useDispatch();

  const [isOpen, setIsOpen] = useState(false);
  const openModal = useCallback(() => setIsOpen(true), []);
  const closeModal = useCallback(() => setIsOpen(false), []);
  const userEmail = useSelector(getCurrentMACContactEmail) ?? '';
  const userOrgDetails = useSelector(getCurrentMACContactOrganizationDetails);
  const detailsData: any = useSelector((state: State) => selectDetails(state, sen));
  const accountDetailsProgress = useSelector((state: State) => selectDetailsProgress(state, sen));
  const genericPropertyObject = useSelector(selectGenericProperty);

  const [canUserSeeAccountDetails, setCanUserSeeAccountDetails] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(true);
  const [isDetailsError, setIsDetailsError] = useState(false);

  const accountDetails = useMemo(
    () => (detailsData && detailsData === 'Access denied' ? null : detailsData),
    [detailsData],
  );

  const mainTechnicalContact = useMemo(
    () => getMainContact(accountDetails?.contactRelationships, ContactTypes.TECHNICAL),
    [accountDetails],
  );

  const mainBillingContact = useMemo(
    () => getMainContact(accountDetails?.contactRelationships, ContactTypes.BILLING),
    [accountDetails],
  );

  const technicalContacts = getContacts(
    accountDetails,
    ContactTypes.TECHNICAL,
    mainTechnicalContact,
    userEmail,
  );

  const billingContacts = getContacts(
    accountDetails,
    ContactTypes.BILLING,
    mainBillingContact,
    userEmail,
  );

  useEffect(() => {
    if (accountDetailsProgress?.inProgress) {
      setIsLoadingDetails(true);
      setIsDetailsError(false);
    } else {
      setIsLoadingDetails(false);
    }
    if (accountDetailsProgress?.error) {
      setIsDetailsError(true);
    }
  }, [accountDetailsProgress]);

  useEffect(() => {
    setCanUserSeeAccountDetails(!(detailsData && detailsData === 'Access denied'));
  }, [detailsData]);

  useEffect(() => {
    if (accountDetails) {
      const productPackage =
        accountDetails.merchandise.productPackage || accountDetails.productPackage;
      if (productPackage?.downloadProduct?.key) {
        genericPropertyFullKey = `related.source.product.${productPackage.downloadProduct.key}`;
      }
      if (genericPropertyFullKey && !genericPropertyObject?.[genericPropertyFullKey]?._result) {
        dispatch(fetchGenericProperty(genericPropertyFullKey));
      }
      if (!accountDetails.hostedType) {
        dispatch(fetchAccountHistory(sen, 0, 10));
      }
    }
  }, [accountDetails]);

  if (isLoadingDetails) {
    return (
      <StyledTableData colSpan={5}>
        <Loading />
      </StyledTableData>
    );
  }

  if (isDetailsError) {
    return (
      <StyledTableData $paddingLeft colSpan={5}>
        <StyledSectionMessageWrapper>
          <SectionMessage appearance="error">
            <FormattedMessage
              id="mac.licenses.account-details.error"
              defaultMessage="Something went wrong. Please try again."
            />
          </SectionMessage>
        </StyledSectionMessageWrapper>
      </StyledTableData>
    );
  }

  if (
    isPartner &&
    detailsData?.entSubMigrated &&
    userOrgDetails?.id === detailsData?.latestPeriod?.partnerId
  ) {
    return (
      <StyledTableData $paddingLeft colSpan={5}>
        <StyledSectionMessageWrapper>
          <SectionMessage appearance="information">
            {t(
              {
                defaultMessage:
                  'This license has been moved to the new billing system. View and manage it in the <ppcLink>Partner Purchasing Center</ppcLink>',
                id: 'mac.licenses.migrated-license-banner',
              },
              {
                ppcLink: (chunks) =>
                  getPpcLink(
                    chunks,
                    accountDetails.transactionAccountId,
                    accountDetails.externalUuid,
                  ),
              },
            )}
          </SectionMessage>
        </StyledSectionMessageWrapper>
      </StyledTableData>
    );
  }

  if (accountDetails?.entSubMigrated) {
    return (
      <MigratedSubscriptionBanner accountDetails={accountDetails} userOrgDetails={userOrgDetails} />
    );
  }

  return (
    <>
      {!canUserSeeAccountDetails ? (
        <StyledTableData $paddingLeft colSpan={5}>
          <StyledSectionMessageWrapper>
            <SectionMessage appearance="error">
              <FormattedMessage {...messages.noPermissionsToSeeDetails} />
            </SectionMessage>
          </StyledSectionMessageWrapper>
        </StyledTableData>
      ) : (
        <DetailsRowContentWithLoader
          accountDetails={accountDetails}
          technicalContacts={technicalContacts}
          billingContacts={billingContacts}
          isAccount={isAccount}
          isOpen={isOpen}
          openModal={openModal}
          closeModal={closeModal}
          isArchived={isArchived}
        />
      )}
    </>
  );
};

export default LicensesExpandedRow;
